import {
  array,
  boolean,
  date,
  email,
  maxLength,
  nonEmpty,
  nullable,
  number,
  object,
  optional,
  pipe,
  record,
  regex,
  string,
  trim,
} from 'valibot';

import type { IFile } from '@model/file';
import type { IUser } from '@model/userProfile';

import type { TEnvironment } from './environmentsOptions';

export const WARRANTY_FIELDS = {
  // PERSONAL INFORMATION STEP
  FULL_NAME: 'fullName',
  JOB_TITLE: 'title',
  EMAIL_ADDRESS: 'email',
  CONTACT_PHONE: 'phone',
  COMPANY_NAME_AND_FACILITY_NAME: 'companyFacility',
  SHIPPING_ADDRESS: 'shippingAddress',

  // DEVICE INFORMATION STEP
  PRODUCT_TYPE: 'productType',
  DEVICE_ID_NUMBER: 'deviceNumber',
  EQUIPMENT_LOCATION: 'equipmentLocation',
  INSTALL_DATE: 'installDate',
  WHAT_ENVIRONMENT: 'whatEnvironment',
  OTHER_ENVIRONMENT: 'other',

  //GENERAL DESCRIPTION
  DESCRIPTION: 'description',
  ATTACHMENTS: 'attachments',
} as const;

export interface IWarrantyForm {
  // PERSONAL INFORMATION STEP
  fullName: string;
  title?: string;
  email: string;
  phone: string;
  companyFacility: string;
  shippingAddress: string;

  // DEVICE INFORMATION STEP
  productType?: string;
  deviceNumber: string;
  equipmentLocation?: string;
  installDate?: Date | null;
  whatEnvironment: Record<TEnvironment, boolean>;
  other?: string;

  //GENERAL DESCRIPTION
  description: string;
  attachments: IFile[];
}

const phoneRegex = /^\+?[0-9 ()-]+$/;

export const schemaPersonalInformation = object({
  [WARRANTY_FIELDS.FULL_NAME]: pipe(string(), nonEmpty('This field is required.')),
  [WARRANTY_FIELDS.JOB_TITLE]: optional(string()),
  [WARRANTY_FIELDS.EMAIL_ADDRESS]: pipe(
    string(),
    trim(),
    nonEmpty('This field is required.'),
    email('Enter correct email'),
    maxLength(255, 'Your email is too long.'),
  ),
  [WARRANTY_FIELDS.CONTACT_PHONE]: pipe(
    string(),
    trim(),
    nonEmpty('This field is required.'),
    regex(phoneRegex, 'Enter correct phone number'),
  ),
  [WARRANTY_FIELDS.COMPANY_NAME_AND_FACILITY_NAME]: pipe(string(), nonEmpty('This field is required.')),
  [WARRANTY_FIELDS.SHIPPING_ADDRESS]: pipe(string(), nonEmpty('This field is required.')),
});

export const schemaDeviceInformation = object({
  [WARRANTY_FIELDS.PRODUCT_TYPE]: optional(string()),
  [WARRANTY_FIELDS.DEVICE_ID_NUMBER]: pipe(string(), nonEmpty('This field is required.')),
  [WARRANTY_FIELDS.EQUIPMENT_LOCATION]: optional(string()),
  [WARRANTY_FIELDS.INSTALL_DATE]: optional(nullable(date())),
  [WARRANTY_FIELDS.WHAT_ENVIRONMENT]: record(string(), optional(boolean())),
  [WARRANTY_FIELDS.OTHER_ENVIRONMENT]: optional(string()),
});

export const schemaGeneralDescription = object({
  [WARRANTY_FIELDS.DESCRIPTION]: pipe(string(), nonEmpty('This field is required.')),
  [WARRANTY_FIELDS.ATTACHMENTS]: array(
    object({ extension: string(), name: string(), size: number(), url: string() }),
  ),
});

export const getWarrantyDefaultValues = (user?: IUser) => {
  return {
    // PERSONAL INFORMATION STEP
    [WARRANTY_FIELDS.FULL_NAME]: user?.full_name || '',
    [WARRANTY_FIELDS.JOB_TITLE]: user?.job_title || '',
    [WARRANTY_FIELDS.EMAIL_ADDRESS]: user?.email || '',
    [WARRANTY_FIELDS.CONTACT_PHONE]: user?.phone || '',
    [WARRANTY_FIELDS.COMPANY_NAME_AND_FACILITY_NAME]: '',
    [WARRANTY_FIELDS.SHIPPING_ADDRESS]: '',

    // DEVICE INFORMATION STEP
    [WARRANTY_FIELDS.DEVICE_ID_NUMBER]: '',
    [WARRANTY_FIELDS.WHAT_ENVIRONMENT]: {},
    [WARRANTY_FIELDS.OTHER_ENVIRONMENT]: '',

    //GENERAL DESCRIPTION
    [WARRANTY_FIELDS.DESCRIPTION]: '',
    [WARRANTY_FIELDS.ATTACHMENTS]: [],
  };
};
